import { useCallback, useRef, useState } from 'react';
import type {
  IncidentEvidenceMoment,
  IncidentEvidenceVideoIdentity,
} from '../../pages/incidents/useIncidentEvidence';
import type { SafetyEpisodeEvidenceMoment } from './IncidentSafetyEpisodeTimeline';

type UseIncidentEvidenceMomentArgs = {
  latestVideoIdentity: IncidentEvidenceVideoIdentity | null;
  latestVideoObjectUrl: string | null;
  loadEvidenceMoment: (
    moment: IncidentEvidenceMoment,
  ) => Promise<IncidentEvidenceVideoIdentity>;
};

export function useIncidentEvidenceMoment({
  latestVideoIdentity,
  latestVideoObjectUrl,
  loadEvidenceMoment,
}: UseIncidentEvidenceMomentArgs) {
  const evidencePlayerCardRef = useRef<HTMLDivElement | null>(null);
  const evidencePlayerRef = useRef<HTMLVideoElement | null>(null);
  const pendingEvidenceMomentRef = useRef<
    (SafetyEpisodeEvidenceMoment & { objectUrl: string }) | null
  >(null);
  const [evidenceMomentError, setEvidenceMomentError] = useState<string | null>(null);

  const focusEvidencePlayer = useCallback(() => {
    evidencePlayerCardRef.current?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
    });
    evidencePlayerRef.current?.focus();
  }, []);

  const seekLoadedEvidenceMoment = useCallback(() => {
    const pending = pendingEvidenceMomentRef.current;
    const player = evidencePlayerRef.current;
    if (!pending || !player) return;
    const renderedSrc = player.currentSrc || player.getAttribute('src') || '';
    if (
      !pending.objectUrl
      || renderedSrc !== pending.objectUrl
      || latestVideoIdentity?.objectUrl !== pending.objectUrl
      || latestVideoIdentity?.clipId !== pending.clipId
    ) {
      setEvidenceMomentError(
        'The loaded clip did not match the selected evidence moment.',
      );
      return;
    }
    try {
      player.currentTime = pending.offsetMs / 1000;
      pendingEvidenceMomentRef.current = null;
      setEvidenceMomentError(null);
      focusEvidencePlayer();
    } catch {
      setEvidenceMomentError(
        'The selected evidence moment could not be positioned in this clip.',
      );
    }
  }, [focusEvidencePlayer, latestVideoIdentity]);

  const handleEvidencePlayerError = useCallback(() => {
    if (pendingEvidenceMomentRef.current) {
      setEvidenceMomentError(
        'The selected evidence moment clip failed to load.',
      );
    }
  }, []);

  const handleViewEvidenceMoment = useCallback(
    async (moment: SafetyEpisodeEvidenceMoment) => {
      const clipId = String(moment?.clipId || '').trim();
      const offsetMs = moment?.offsetMs;
      if (
        !clipId
        || offsetMs === null
        || offsetMs === undefined
        || !Number.isFinite(offsetMs)
        || offsetMs < 0
      ) {
        setEvidenceMomentError(
          'This evidence moment does not include a valid clip position.',
        );
        return;
      }
      setEvidenceMomentError(null);
      focusEvidencePlayer();

      if (
        latestVideoIdentity?.clipId === clipId
        && latestVideoIdentity.objectUrl === latestVideoObjectUrl
        && evidencePlayerRef.current
      ) {
        pendingEvidenceMomentRef.current = {
          clipId,
          offsetMs,
          objectUrl: latestVideoIdentity.objectUrl,
        };
        seekLoadedEvidenceMoment();
        return;
      }

      try {
        const identity = await loadEvidenceMoment({ clipId, offsetMs });
        if (identity?.clipId !== clipId || !identity?.objectUrl) {
          throw new Error(
            'The loaded clip did not match the selected evidence moment.',
          );
        }
        pendingEvidenceMomentRef.current = {
          clipId,
          offsetMs,
          objectUrl: identity.objectUrl,
        };
        focusEvidencePlayer();
      } catch (error: any) {
        setEvidenceMomentError(
          error?.message || 'Failed to load the selected evidence moment.',
        );
      }
    },
    [
      focusEvidencePlayer,
      latestVideoIdentity,
      latestVideoObjectUrl,
      loadEvidenceMoment,
      seekLoadedEvidenceMoment,
    ],
  );

  return {
    evidencePlayerCardRef,
    evidencePlayerRef,
    evidenceMomentError,
    handleEvidencePlayerError,
    handleViewEvidenceMoment,
    seekLoadedEvidenceMoment,
  };
}
